import { create } from "zustand";
import { apiFetch } from "@/lib/api";
import type { ContactType } from "@/components/sartn/ContactCard";

interface ContactsApiResponse {
  contacts: ContactType[];
  total: number;
  page: number;
  pageSize: number;
}

interface ContactState {
  contacts: ContactType[];
  selectedContact: ContactType | null;
  isLoading: boolean;
  error: string | null;
  searchQuery: string;
  activeCategory: string;
  page: number;
  totalContacts: number;
}

interface ContactActions {
  fetchContacts: (category?: string, search?: string, page?: number) => Promise<void>;
  fetchContactDetail: (id: number) => Promise<void>;
  createContact: (data: CreateContactPayload) => Promise<void>;
  updateContactCategory: (id: number, category: string) => Promise<void>;
  toggleBlockContact: (id: number, blocked: boolean) => Promise<void>;
  deleteContact: (id: number) => Promise<void>;
  setSearchQuery: (query: string) => void;
  setActiveCategory: (category: string) => void;
  clearSelectedContact: () => void;
  clearError: () => void;
}

interface CreateContactPayload {
  firstName: string;
  lastName?: string;
  phone?: string;
  category: string;
  notes?: string;
}

export const useContactStore = create<ContactState & ContactActions>((set, get) => ({
  contacts: [],
  selectedContact: null,
  isLoading: false,
  error: null,
  searchQuery: "",
  activeCategory: "all",
  page: 1,
  totalContacts: 0,

  fetchContacts: async (category, search, page = 1) => {
    set({ isLoading: true, error: null });

    const params = new URLSearchParams();
    if (category && category !== "all") params.set("category", category);
    if (search) params.set("search", search);
    params.set("page", String(page));

    try {
      const queryString = params.toString();
      const path = `/api/contacts${queryString ? `?${queryString}` : ""}`;
      const data = await apiFetch<ContactsApiResponse>(path);

      set({
        contacts: data.contacts,
        totalContacts: data.total,
        page: data.page,
        isLoading: false,
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load contacts";
      set({ error: message, isLoading: false });
    }
  },

  fetchContactDetail: async (id) => {
    set({ isLoading: true, error: null });

    try {
      const contact = await apiFetch<ContactType>(`/api/contacts/${id}`);
      set({ selectedContact: contact, isLoading: false });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load contact";
      set({ error: message, isLoading: false });
    }
  },

  createContact: async (data) => {
    set({ isLoading: true, error: null });

    try {
      await apiFetch("/api/contacts", { method: "POST", body: data });
      const { activeCategory, searchQuery, page } = get();
      await get().fetchContacts(activeCategory, searchQuery, page);
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to create contact";
      set({ error: message, isLoading: false });
      throw err;
    }
  },

  updateContactCategory: async (id, category) => {
    try {
      const updated = await apiFetch<ContactType>(`/api/contacts/${id}`, {
        method: "PATCH",
        body: { category },
      });
      set({ selectedContact: updated });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update category";
      set({ error: message });
    }
  },

  toggleBlockContact: async (id, blocked) => {
    try {
      const updated = await apiFetch<ContactType>(`/api/contacts/${id}`, {
        method: "PATCH",
        body: { isBlocked: blocked },
      });
      set({ selectedContact: updated });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to update contact";
      set({ error: message });
    }
  },

  deleteContact: async (id) => {
    try {
      await apiFetch(`/api/contacts/${id}`, { method: "DELETE" });
      set({ selectedContact: null });
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to delete contact";
      set({ error: message });
      throw err;
    }
  },

  setSearchQuery: (query) => set({ searchQuery: query }),
  setActiveCategory: (category) => set({ activeCategory: category }),
  clearSelectedContact: () => set({ selectedContact: null }),
  clearError: () => set({ error: null }),
}));

export type { CreateContactPayload };
